import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { Router } from '@angular/router';

import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { MatInputComponent } from '../../shared/mat-input/mat-input.component';
import {
  MatDropdownComponent,
  DropdownOption,
} from '../../shared/mat-dropdown/mat-dropdown.component';
import {
  MatTableComponent,
  TableColumn,
} from '../../shared/mat-table/mat-table.component';
import { MatDatePickerComponent } from '../../shared/mat-date-picker/mat-date-picker.component';
import { BillingService } from '../../service/billing.service';
import { RateCardService } from '../../service/rate-card.service';
import { AuthService } from '../../auth/services/auth.service';
import {
  TripData,
  TripFilters,
  FilterSuggestionsResponse,
  FilteredTripsResponse,
  FilterSuggestionsDto,
  RouteSuggestion,
  VendorSuggestion,
  DriverSuggestion,
  VehicleSuggestion,
} from '../../models/billing.model';

@Component({
  selector: 'app-billing-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatChipsModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PageHeaderComponent,
    MatInputComponent,
    MatDropdownComponent,
    MatTableComponent,
    MatDatePickerComponent,
  ],
  templateUrl: './billing-management.component.html',
  styleUrls: ['./billing-management.component.scss'],
})
export class BillingManagementComponent implements OnInit {
  filterForm: FormGroup;
  trips: TripData[] = [];
  selectedTrips: TripData[] = [];
  selectedTripIds: Set<string> = new Set();

  // Filter options
  routes: RouteSuggestion[] = [];
  vendors: VendorSuggestion[] = [];
  drivers: DriverSuggestion[] = [];
  vehicles: VehicleSuggestion[] = [];
  vehicleSizes: DropdownOption[] = [];
  typeOptions: DropdownOption[] = [
    { value: 'MARKET', label: 'Market' },
    { value: 'VENDOR', label: 'Vendor' },
  ];

  loading = false;
  loadingFilters = false;
  error = '';
  searchText = '';

  // Pagination
  totalElements = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Column definitions for the table
  columns: TableColumn[] = [
    {
      name: 'type',
      header: 'Trip type',
      type: 'text',
      valueFormatter: (row: any) => row.vehicleDetails?.type || '',
    },
    {
      name: 'tripId',
      header: 'Trip ID',
      type: 'text',
      sortable: true,
    },
    {
      name: 'routeName',
      header: 'Route Name',
      type: 'text',
      sortable: true,
    },
    {
      name: 'vendorName',
      header: 'Vendor',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) => row.vehicleDetails?.vendorName || '',
    },
    {
      name: 'driverName',
      header: 'Driver',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) => row.driverDetails?.driverName || '',
    },
    {
      name: 'tripStartDate',
      header: 'Trip Start Date',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) => {
        if (!row.tripStartDate) return '';
        return new Date(row.tripStartDate).toLocaleDateString('en-IN');
      },
    },
    {
      name: 'vehicleNumber',
      header: 'Vehicle Number',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) =>
        `${row.vehicleDetails?.vehicleNumber} [${
          row.vehicleDetails?.fleetType || ''
        }]`,
    },
    {
      name: 'fleetType',
      header: 'Vehicle Size',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) => row.vehicleDetails?.fleetType || '',
    },
    {
      name: 'distance',
      header: 'Distance (km)',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) =>
        row.distance ? `${row.distance} km` : 'N/A',
    },
    {
      name: 'rateCardType',
      header: 'Rate Card Type',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) => {
        const rateType = this.rateCardService
          .getRateTypes()
          .find((type) => type.value === row.rateCardType);
        return rateType ? rateType.label : row.rateCardType || 'N/A';
      },
    },
    {
      name: 'price',
      header: 'Rate',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) =>
        row.price ? `₹${row.price.toLocaleString()}` : 'N/A',
    },
    {
      name: 'cost',
      header: 'Total Amount',
      type: 'text',
      sortable: true,
      valueFormatter: (row: any) =>
        `₹${row.vehicleDetails?.cost?.toLocaleString() || 0}`,
    },
  ];

  displayedColumns = [
    'select',
    'type',
    'tripId',
    'tripStartDate',
    'routeName',
    'vendorName',
    'driverName',
    'vehicleNumber',
    // 'fleetType',
    'rateCardType',
    'distance',
    'price',
    'cost',
  ];

  constructor(
    private fb: FormBuilder,
    private billingService: BillingService,
    private rateCardService: RateCardService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.filterForm = this.fb.group({
      type: ['', Validators.required], // Make type mandatory
      routeId: [''],
      vendorId: [''],
      driverId: [''],
      driverName: [''],
      vehicleId: [''],
      vehicleNumber: [''],
      vehicleSize: [''],
      fromDate: [''],
      toDate: [''],
    });

    // Add conditional validation for vendor when type is VENDOR
    this.filterForm.get('type')?.valueChanges.subscribe((type) => {
      const vendorControl = this.filterForm.get('vendorId');
      if (type === 'VENDOR') {
        vendorControl?.setValidators([Validators.required]);
      } else {
        vendorControl?.clearValidators();
        vendorControl?.setValue('');
        vendorControl?.markAsUntouched(); // Ensure vendor is not touched when type is not VENDOR
      }
      vendorControl?.updateValueAndValidity();
    });
  }

  ngOnInit() {
    // Ensure vendor field starts in clean state
    this.resetVendorValidation();

    // Restore state if coming back from trip summary
    const savedState = sessionStorage.getItem('billingManagementState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.selectedTrips = state.selectedTrips || [];
        this.selectedTripIds = new Set(state.selectedTripIds || []);
        this.currentPage = state.currentPage || 0;
        this.pageSize = state.pageSize || 10;
        this.totalElements = state.totalElements || 0;
        this.trips = state.trips || [];

        // Restore filter form
        if (state.filterForm) {
          this.filterForm.patchValue(state.filterForm);
          // Re-apply vendor validation based on restored type
          this.resetVendorValidation();
        }

        // Clear the saved state
        sessionStorage.removeItem('billingManagementState');
      } catch (error) {
        console.error('Error restoring billing management state:', error);
        this.loadFilterSuggestions();
      }
    } else {
      this.loadFilterSuggestions();
    }
  }

  resetVendorValidation() {
    const type = this.filterForm.get('type')?.value;
    const vendorControl = this.filterForm.get('vendorId');

    if (type !== 'VENDOR') {
      vendorControl?.clearValidators();
      vendorControl?.setValue('');
      vendorControl?.markAsUntouched();
      vendorControl?.updateValueAndValidity();
    }
  }

  loadFilterSuggestions(filters?: FilterSuggestionsDto) {
    this.loadingFilters = true;
    this.error = '';

    // Use provided filters or get current form filters
    const currentFilters = filters || this.getCurrentFilters();

    this.billingService.getFilterSuggestions(currentFilters).subscribe({
      next: (response: FilterSuggestionsResponse) => {
        // Filter out null values and map the data properly
        this.routes = (response.data.routes || []).filter(
          (route) => route.routeId && route.routeName
        );

        this.vendors = (response.data.vendorDetails || []).filter(
          (vendor) => vendor.vendorIds && vendor.vendorNames
        );

        // For drivers, include those with names even if IDs are empty (for MARKET trips)
        this.drivers = (response.data.driverDetails || []).filter(
          (driver) => driver.driverNames
        );

        // For vehicles, include those with vehicle numbers even if IDs are empty (for MARKET trips)
        this.vehicles = (response.data.vehicleDetails || []).filter(
          (vehicle) => vehicle.vehicleNumbers && vehicle.fleetTypes
        );

        // Extract unique vehicle sizes
        const uniqueSizes = new Set(
          this.vehicles.map((vehicle) => vehicle.fleetTypes)
        );
        this.vehicleSizes = Array.from(uniqueSizes).map((size) => ({
          value: size,
          label: size,
        }));

        this.loadingFilters = false;
      },
      error: (error) => {
        console.error('Error loading filter suggestions:', error);
        this.error = 'Failed to load filter suggestions';
        this.loadingFilters = false;
      },
    });
  }

  getCurrentFilters(): FilterSuggestionsDto {
    const formValue = this.filterForm.value;
    return {
      type: formValue.type || undefined,
      routeId: formValue.routeId || undefined,
      vendorId: formValue.vendorId || undefined,
      driverId: formValue.driverId || undefined,
      vehicleId: formValue.vehicleId || undefined,
      fleetType: formValue.vehicleSize || undefined,
      // Note: fromDate and toDate are excluded as they don't affect filter suggestions
    };
  }

  onTypeChange() {
    // Clear dependent filters when type changes
    this.filterForm.patchValue({
      routeId: '',
      vendorId: '',
      driverId: '',
      vehicleId: '',
      vehicleSize: '',
    });

    // Update vendor validation based on type
    const type = this.filterForm.get('type')?.value;
    const vendorControl = this.filterForm.get('vendorId');
    if (type === 'VENDOR') {
      vendorControl?.setValidators([Validators.required]);
    } else {
      vendorControl?.clearValidators();
      vendorControl?.setValue('');
      // Reset vendor touch state when type is not VENDOR
      vendorControl?.markAsUntouched();
    }
    vendorControl?.updateValueAndValidity();

    // Pass current filters (including the newly selected type)
    this.loadFilterSuggestions(this.getCurrentFilters());
  }

  onTypeBlur() {
    // When type field loses focus, ensure vendor validation is properly set
    const type = this.filterForm.get('type')?.value;
    const vendorControl = this.filterForm.get('vendorId');

    if (type !== 'VENDOR') {
      // If type is not VENDOR, ensure vendor is not marked as touched
      vendorControl?.markAsUntouched();
    }
  }

  onRouteChange() {
    // Clear dependent filters when route changes
    this.filterForm.patchValue({
      vendorId: '',
      driverId: '',
      vehicleId: '',
      vehicleSize: '',
    });
    // Pass current filters (including the newly selected route)
    this.loadFilterSuggestions(this.getCurrentFilters());
  }

  onVendorChange() {
    // Clear dependent filters when vendor changes
    this.filterForm.patchValue({
      driverId: '',
      vehicleId: '',
      vehicleSize: '',
    });
    // Pass current filters (including the newly selected vendor)
    this.loadFilterSuggestions(this.getCurrentFilters());
  }

  onDriverChange() {
    // Clear dependent filters when driver changes
    this.filterForm.patchValue({
      vehicleId: '',
      vehicleNumber: '',
      vehicleSize: '',
    });

    // Set driverName based on selected driver
    const selectedDriverId = this.filterForm.get('driverId')?.value;
    if (selectedDriverId) {
      const selectedDriver = this.drivers.find(
        (d) =>
          d.driverIds === selectedDriverId || d.driverNames === selectedDriverId
      );
      if (selectedDriver) {
        this.filterForm.patchValue({
          driverName: selectedDriver.driverNames,
        });
      }
    }

    // Pass current filters (including the newly selected driver)
    this.loadFilterSuggestions(this.getCurrentFilters());
  }

  onVehicleChange() {
    // Clear dependent filters when vehicle changes
    this.filterForm.patchValue({
      vehicleSize: '',
    });

    // Set vehicleNumber based on selected vehicle
    const selectedVehicleId = this.filterForm.get('vehicleId')?.value;
    if (selectedVehicleId) {
      const selectedVehicle = this.vehicles.find(
        (v) =>
          v.vehicleId === selectedVehicleId ||
          v.vehicleNumbers === selectedVehicleId
      );
      if (selectedVehicle) {
        this.filterForm.patchValue({
          vehicleNumber: selectedVehicle.vehicleNumbers,
        });
      }
    }

    // Pass current filters (including the newly selected vehicle)
    this.loadFilterSuggestions(this.getCurrentFilters());
  }

  onVehicleSizeChange() {
    // Clear dependent filters when vehicle size changes
    this.filterForm.patchValue({
      vehicleId: '',
    });
    // Pass current filters (including the newly selected vehicle size)
    this.loadFilterSuggestions(this.getCurrentFilters());
  }

  loadAllTrips() {
    this.loading = true;
    this.error = '';

    // Get current form values to include type filter if set
    const formValue = this.filterForm.value;
    const filters: TripFilters = {
      type: formValue.type || undefined,
    };

    this.billingService.getFilteredTrips(filters).subscribe({
      next: (response) => {
        this.trips = response.data || [];
        this.totalElements = response.data?.length || 0;

        // Clear selection when new data is loaded
        this.selectedTrips = [];
        this.selectedTripIds.clear();

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading trips:', error);
        this.error = 'Failed to load trips';
        this.loading = false;
      },
    });
  }

  applyFilters() {
    // Check if form is valid
    if (this.filterForm.invalid) {
      // Only mark the invalid fields as touched
      const typeControl = this.filterForm.get('type');
      const vendorControl = this.filterForm.get('vendorId');

      if (typeControl?.invalid) {
        typeControl.markAsTouched();
      }

      // Only mark vendor as touched if type is specifically 'VENDOR' and vendor is actually invalid
      if (
        this.filterForm.get('type')?.value === 'VENDOR' &&
        vendorControl?.invalid
      ) {
        vendorControl.markAsTouched();
      } else if (this.filterForm.get('type')?.value !== 'VENDOR') {
        // If type is not VENDOR, ensure vendor is not touched
        vendorControl?.markAsUntouched();
      }

      this.snackBar.open('Please fill in all required fields', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
      return;
    }

    const formValue = this.filterForm.value;
    const fromDate = formValue.fromDate;
    const toDate = formValue.toDate;
    const type = formValue.type;

    console.log('Form values:', formValue);
    console.log('Type value:', type);

    // Validate date range
    if (fromDate && toDate && fromDate > toDate) {
      this.snackBar.open('From date cannot be after to date', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
      return;
    }

    this.loading = true;
    this.error = '';

    const filters: TripFilters = {
      type: type || undefined,
      routeId: formValue.routeId || undefined,
      vendorId: formValue.vendorId || undefined,
      vehicleSize: formValue.vehicleSize || undefined,
      dateRange:
        fromDate && toDate
          ? {
              startDate: fromDate,
              endDate: toDate,
            }
          : undefined,
    };

    // Only send driverId if driverName is not available
    if (formValue.driverName) {
      filters.driverName = formValue.driverName;
    } else if (formValue.driverId) {
      filters.driverId = formValue.driverId;
    }

    // Only send vehicleId if vehicleNumber is not available
    if (formValue.vehicleNumber) {
      filters.vehicleNumber = formValue.vehicleNumber;
    } else if (formValue.vehicleId) {
      filters.vehicleId = formValue.vehicleId;
    }

    console.log('Sending filters to API:', filters);

    this.billingService.getFilteredTrips(filters).subscribe({
      next: (response) => {
        this.trips = response.data || [];
        this.totalElements = response.data?.length || 0;

        // Clear selection when filters are applied
        this.selectedTrips = [];
        this.selectedTripIds.clear();

        this.loading = false;
      },
      error: (error) => {
        console.error('Error applying filters:', error);
        this.error = 'Failed to apply filters';
        this.loading = false;
      },
    });
  }

  clearFilters() {
    this.filterForm.reset();
    this.selectedTrips = [];
    this.selectedTripIds.clear();
    this.currentPage = 0;

    // Reload filter suggestions and all trips when filters are cleared
    this.loadFilterSuggestions();
    // this.loadAllTrips();
  }

  onSearchChange(event: any) {
    this.searchText = event.target.value;
    // Implement search functionality if needed
  }

  onSelectionChange(selected: TripData[]) {
    // The shared component sends the entire row objects, but we need to track by ID
    // Clear the current selection and rebuild it based on the new selection
    this.selectedTripIds.clear();
    this.selectedTrips = [];

    // Add the newly selected trips to our tracking
    selected.forEach((trip) => {
      // Try different possible ID properties
      const tripId = trip._id || trip.tripId;

      if (tripId) {
        this.selectedTripIds.add(tripId);
        this.selectedTrips.push(trip);
      }
    });
  }

  onRowClick(row: TripData) {
    // Handle row click if needed
  }

  onSortChange(sort: any) {
    // Handle sort if needed
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;

    // Reload data with current filters
    this.applyFilters();
  }

  getRouteOptions(): DropdownOption[] {
    return this.routes.map((route) => ({
      value: route.routeId,
      label: route.routeName,
    }));
  }

  getVendorOptions(): DropdownOption[] {
    return this.vendors.map((vendor) => ({
      value: vendor.vendorIds,
      label: vendor.vendorNames,
    }));
  }

  getDriverOptions(): DropdownOption[] {
    return this.drivers
      .filter((driver) => driver.driverNames) // Only include drivers with names
      .map((driver) => ({
        value: driver.driverIds || driver.driverNames, // Use name as value if ID is empty
        label: driver.driverNames,
        // Store the driver name for later use
        data: { driverName: driver.driverNames },
      }));
  }

  getVehicleOptions(): DropdownOption[] {
    return this.vehicles
      .filter((vehicle) => vehicle.vehicleNumbers) // Only include vehicles with numbers
      .map((vehicle) => ({
        value: vehicle.vehicleId || vehicle.vehicleNumbers, // Use vehicle number as value if ID is empty
        label: vehicle.vehicleNumbers,
        // Store the vehicle number for later use
        data: { vehicleNumber: vehicle.vehicleNumbers },
      }));
  }

  generateInvoice() {
    // Invoice generation logic can be implemented here
    this.snackBar.open('Invoice generation started', 'Close', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.filterForm.get(controlName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return `${controlName} is required`;
      }
    }
    return '';
  }

  onFromDateChange(date: Date | null) {
    // Date changes don't trigger filter suggestions API
    // Clear toDate if fromDate is after current toDate
    const toDate = this.filterForm.get('toDate')?.value;
    if (date && toDate && date > toDate) {
      this.filterForm.patchValue({ toDate: null });
    }
  }

  onToDateChange(date: Date | null) {
    // Date changes don't trigger filter suggestions API
  }

  onNextClick(): void {
    if (this.selectedTrips.length === 0) {
      this.snackBar.open('Please select at least one trip', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
      return;
    }

    // Save current state to session storage for back navigation
    const currentState = {
      selectedTrips: this.selectedTrips,
      filters: this.filterForm.value,
      pagination: {
        pageIndex: this.currentPage,
        pageSize: this.pageSize,
      },
    };
    sessionStorage.setItem(
      'billingManagementState',
      JSON.stringify(currentState)
    );

    // Navigate to trip summary with data in router state
    this.router.navigate(['/BillingManagement/trip-summary'], {
      state: {
        selectedTrips: this.selectedTrips,
        filters: this.filterForm.value,
      },
    });
  }

  getMaxFromDate(): Date {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return today;
  }

  getMinToDate(): Date | null {
    const fromDate = this.filterForm.get('fromDate')?.value;
    if (!fromDate) return null;

    const minDate = new Date(fromDate);
    minDate.setHours(0, 0, 0, 0); // Start of the from date
    return minDate;
  }

  getMaxToDate(): Date {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return today;
  }

  isTripSelected(tripId: string): boolean {
    return this.selectedTripIds.has(tripId);
  }

  getSelectedCount(): number {
    return this.selectedTrips.length;
  }

  // Helper method to mark all form controls as touched
  markFormGroupTouched() {
    Object.keys(this.filterForm.controls).forEach((key) => {
      const control = this.filterForm.get(key);
      control?.markAsTouched();
    });
  }
}
